import * as XLSX from 'xlsx';
import { UserPerformanceData } from '../lib/types';
import { format } from 'date-fns';

interface ExportData {
  users: UserPerformanceData[];
  aggregate: {
    totalPnl: number;
    totalUsers: number;
    totalTrades: number;
    averageWinRate: number;
  };
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

const formatCurrency = (value: number) => {
  return value.toFixed(2);
};

const formatPercentage = (value: number) => {
  return value.toFixed(2);
};

const formatDateExcel = (timestamp: string) => {
  const date = new Date(parseInt(timestamp));
  return format(date, 'yyyy-MM-dd HH:mm:ss');
};

const calculateTimeInMarketHours = (entryTime: string, exitTime: string) => {
  const entry = parseInt(entryTime);
  const exit = parseInt(exitTime);
  const diffMs = exit - entry;
  return diffMs / (1000 * 60 * 60);
};

export const exportTradingDataToExcel = (data: ExportData, fileName?: string) => {
  const workbook = XLSX.utils.book_new();
  
  // 1. Summary Sheet
  const summaryData = [
    ['Trading Performance Summary'],
    [],
    ['Report Generated', format(new Date(), 'yyyy-MM-dd HH:mm:ss')],
    ['Date Range', data.dateRange?.from && data.dateRange?.to ? 
      `${format(data.dateRange.from, 'yyyy-MM-dd')} to ${format(data.dateRange.to, 'yyyy-MM-dd')}` : 'All Time'],
    [],
    ['Aggregate Metrics'],
    ['Total P&L (USDT)', formatCurrency(data.aggregate.totalPnl)],
    ['Total Users', data.aggregate.totalUsers],
    ['Total Trades', data.aggregate.totalTrades],
    ['Average Win Rate (%)', formatPercentage(data.aggregate.averageWinRate)],
    [],
    ['User Performance Summary'],
    ['User', 'Total P&L', 'Win Rate (%)', 'Total Trades', 'Total Volume', 'Largest Win', 'Largest Loss', 'Avg Trade Size', 'Avg Holding Hours']
  ];

  data.users.forEach(user => {
    const avgHoldingHours = user.closedPnL.length > 0 
      ? user.closedPnL.reduce((sum, trade) => 
          sum + calculateTimeInMarketHours(trade.createdTime, trade.updatedTime), 0) / user.closedPnL.length
      : 0;
    
    summaryData.push([
      user.userName,
      formatCurrency(user.totalPnl),
      formatPercentage(user.winRate),
      user.totalTrades,
      formatCurrency(user.totalVolume),
      formatCurrency(user.largestWin),
      formatCurrency(user.largestLoss),
      formatCurrency(user.averageTradeSize),
      avgHoldingHours.toFixed(2)
    ]);
  });

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  
  // Set column widths for summary sheet
  summarySheet['!cols'] = [
    { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
    { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 18 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // 2. Closed Trades Sheet
  const closedTradesData: (string | number)[][] = [
    ['Trader', 'Symbol', 'Side', 'Quantity', 'Entry Price', 'Exit Price', 'Trade Type',
     'Closed P&L (USDT)', 'Result', 'Open Volume', 'Close Volume', 'Opening Fee', 
     'Closing Fee', 'Leverage', 'Time in Market (Hours)', 'Entry Time', 'Exit Time']
  ];

  data.users.forEach(user => {
    user.closedPnL.forEach(trade => {
      const pnl = parseFloat(trade.closedPnl || '0');
      const timeInMarketHours = calculateTimeInMarketHours(trade.createdTime, trade.updatedTime);
      
      closedTradesData.push([
        user.userName,
        trade.symbol,
        trade.side,
        parseFloat(trade.qty || '0'),
        parseFloat(trade.avgEntryPrice || '0'),
        parseFloat(trade.avgExitPrice || '0'),
        trade.orderType || trade.execType || '-',
        formatCurrency(pnl),
        pnl > 0 ? 'Win' : 'Loss',
        formatCurrency(parseFloat(trade.cumEntryValue || '0')),
        formatCurrency(parseFloat(trade.cumExitValue || '0')),
        formatCurrency(parseFloat(trade.openingFee || '0')),
        formatCurrency(parseFloat(trade.closingFee || '0')),
        trade.leverage,
        timeInMarketHours.toFixed(2),
        formatDateExcel(trade.createdTime),
        formatDateExcel(trade.updatedTime)
      ]);
    });
  });

  const closedTradesSheet = XLSX.utils.aoa_to_sheet(closedTradesData);
  
  // Auto-filter for closed trades
  closedTradesSheet['!autofilter'] = { ref: `A1:Q${closedTradesData.length}` };
  
  // Set column widths
  closedTradesSheet['!cols'] = [
    { wch: 15 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 12 },
    { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 8 }, { wch: 12 },
    { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 20 },
    { wch: 20 }, { wch: 20 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, closedTradesSheet, 'Closed Trades');

  // 3. Trade Executions Sheet
  const executionsData: (string | number)[][] = [
    ['Trader', 'Symbol', 'Side', 'Quantity', 'Price', 'Value', 'Fee', 'Type', 'Time']
  ];

  data.users.forEach(user => {
    user.trades.forEach(trade => {
      executionsData.push([
        user.userName,
        trade.symbol,
        trade.side,
        parseFloat(trade.execQty),
        parseFloat(trade.execPrice),
        formatCurrency(parseFloat(trade.execValue)),
        formatCurrency(parseFloat(trade.execFee)),
        trade.isMaker ? 'Maker' : 'Taker',
        formatDateExcel(trade.execTime)
      ]);
    });
  });

  const executionsSheet = XLSX.utils.aoa_to_sheet(executionsData);
  
  // Auto-filter for executions
  executionsSheet['!autofilter'] = { ref: `A1:I${executionsData.length}` };
  
  // Set column widths
  executionsSheet['!cols'] = [
    { wch: 15 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 12 },
    { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 20 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, executionsSheet, 'Trade Executions');

  // 4. Open Positions Sheet
  const openPositionsData: (string | number)[][] = [
    ['Trader', 'Symbol', 'Side', 'Size', 'Entry Price', 'Mark Price', 'Leverage', 
     'Unrealized P&L (USDT)', 'Created Time']
  ];

  data.users.forEach(user => {
    user.positions
      .filter(pos => parseFloat(pos.size) > 0)
      .forEach(position => {
        openPositionsData.push([
          user.userName,
          position.symbol,
          position.side,
          parseFloat(position.size),
          parseFloat(position.avgPrice),
          parseFloat(position.markPrice),
          position.leverage || '-',
          formatCurrency(parseFloat(position.unrealisedPnl || '0')),
          formatDateExcel(position.createdTime)
        ]);
      });
  });

  const positionsSheet = XLSX.utils.aoa_to_sheet(openPositionsData);
  
  // Auto-filter for positions
  positionsSheet['!autofilter'] = { ref: `A1:I${openPositionsData.length}` };
  
  // Set column widths
  positionsSheet['!cols'] = [
    { wch: 15 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 12 },
    { wch: 12 }, { wch: 10 }, { wch: 18 }, { wch: 20 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, positionsSheet, 'Open Positions');

  // 5. Daily P&L Sheet
  const dailyPnLMap = new Map<string, { date: string; pnl: number; trades: number; volume: number }>();
  
  data.users.forEach(user => {
    user.closedPnL.forEach(trade => {
      const dateKey = format(new Date(parseInt(trade.updatedTime)), 'yyyy-MM-dd');
      const existing = dailyPnLMap.get(dateKey) || { date: dateKey, pnl: 0, trades: 0, volume: 0 };
      existing.pnl += parseFloat(trade.closedPnl || '0');
      existing.trades += 1;
      existing.volume += parseFloat(trade.cumExitValue || '0');
      dailyPnLMap.set(dateKey, existing);
    });
  });

  const dailyPnLData: (string | number)[][] = [
    ['Date', 'Total P&L', 'Number of Trades', 'Total Volume', 'Cumulative P&L']
  ];

  let cumulativePnL = 0;
  Array.from(dailyPnLMap.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .forEach(day => {
      cumulativePnL += day.pnl;
      dailyPnLData.push([
        day.date,
        formatCurrency(day.pnl),
        day.trades,
        formatCurrency(day.volume),
        formatCurrency(cumulativePnL)
      ]);
    });

  const dailySheet = XLSX.utils.aoa_to_sheet(dailyPnLData);
  
  // Auto-filter for daily P&L
  dailySheet['!autofilter'] = { ref: `A1:E${dailyPnLData.length}` };
  
  // Set column widths
  dailySheet['!cols'] = [
    { wch: 12 }, { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 18 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, dailySheet, 'Daily P&L');

  // 6. Statistics Sheet
  const statsData: (string | number | undefined)[][] = [
    ['Trading Statistics'],
    [],
    ['By User Statistics']
  ];

  data.users.forEach(user => {
    const wins = user.closedPnL.filter(t => parseFloat(t.closedPnl || '0') > 0).length;
    const losses = user.closedPnL.filter(t => parseFloat(t.closedPnl || '0') <= 0).length;
    const totalWinAmount = user.closedPnL
      .filter(t => parseFloat(t.closedPnl || '0') > 0)
      .reduce((sum, t) => sum + parseFloat(t.closedPnl || '0'), 0);
    const totalLossAmount = user.closedPnL
      .filter(t => parseFloat(t.closedPnl || '0') <= 0)
      .reduce((sum, t) => sum + Math.abs(parseFloat(t.closedPnl || '0')), 0);
    
    statsData.push(
      [],
      [`User: ${user.userName}`],
      ['Metric', 'Value'],
      ['Total Trades', user.totalTrades],
      ['Winning Trades', wins],
      ['Losing Trades', losses],
      ['Win Rate (%)', formatPercentage(user.winRate)],
      ['Total Win Amount', formatCurrency(totalWinAmount)],
      ['Total Loss Amount', formatCurrency(totalLossAmount)],
      ['Average Win', wins > 0 ? formatCurrency(totalWinAmount / wins) : '0'],
      ['Average Loss', losses > 0 ? formatCurrency(totalLossAmount / losses) : '0'],
      ['Profit Factor', losses > 0 && totalLossAmount > 0 ? (totalWinAmount / totalLossAmount).toFixed(2) : 'N/A'],
      ['Largest Win', formatCurrency(user.largestWin)],
      ['Largest Loss', formatCurrency(Math.abs(user.largestLoss))],
      ['Average Trade Size', formatCurrency(user.averageTradeSize)]
    );
  });

  const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
  
  // Set column widths
  statsSheet['!cols'] = [
    { wch: 25 }, { wch: 20 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, statsSheet, 'Statistics');

  // Generate filename with timestamp
  const defaultFileName = `trading_data_export_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
  
  // Write the file
  XLSX.writeFile(workbook, fileName || defaultFileName);
};